import { useSEO } from "@/lib/seo";

const LegalSection = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl md:text-3xl text-[#203240] font-light">{title}</h2>
    <div className="mt-4 text-[#3D4A5A] leading-relaxed space-y-3">{children}</div>
  </section>
);

export function MentionsLegales() {
  useSEO({ title: "Mentions légales", description: "Mentions légales du site L'Entracte Restaurant & Réception à Ville d'Avray." });
  return (
    <article data-testid="legal-page" className="px-6 md:px-12 lg:px-16 max-w-3xl mx-auto py-16 md:py-24">
      <p className="overline">Informations légales</p>
      <h1 className="font-display text-4xl md:text-6xl mt-4 leading-tight text-[#203240]">Mentions légales</h1>

      <LegalSection title="Éditeur du site">
        <p><strong>L'Entracte Restaurant & Réception</strong><br/>
        3 rue de Versailles, 92410 Ville d'Avray, France<br/>
        Téléphone : 01 82 00 18 67<br/>
        Email : restaurantlentractevda@gmail.com</p>
        <p className="text-sm text-[#697585]">Forme juridique, SIRET et numéro de TVA intracommunautaire à compléter par l'éditeur.</p>
      </LegalSection>

      <LegalSection title="Directeur de la publication">
        <p>Le directeur de la publication est le représentant légal de L'Entracte Restaurant & Réception.</p>
      </LegalSection>

      <LegalSection title="Hébergement">
        <p>Le site est hébergé par <strong>Emergent</strong> (infrastructure Google Cloud Platform), service de déploiement et d'hébergement d'applications web.</p>
        <p>Site : <a href="https://emergent.sh" className="link-gold">emergent.sh</a></p>
      </LegalSection>

      <LegalSection title="Propriété intellectuelle">
        <p>L'ensemble des éléments présentés sur le site lentracterestaurant.fr (textes, images, photographies, logos, vidéos, illustrations…) est protégé par le droit d'auteur et le droit des marques.</p>
        <p>Toute reproduction, représentation, modification, publication, transmission ou exploitation de tout ou partie du site, par quelque procédé que ce soit, est strictement interdite sans l'autorisation expresse de L'Entracte.</p>
      </LegalSection>

      <LegalSection title="Crédits photos">
        <p>Photographies : L'Entracte Restaurant & Réception. Tous droits réservés.</p>
      </LegalSection>

      <LegalSection title="Données personnelles">
        <p>Voir notre <a href="/politique-de-confidentialite" className="link-gold">politique de confidentialité</a> pour le détail du traitement de vos données personnelles.</p>
      </LegalSection>

      <LegalSection title="Cookies">
        <p>Voir notre <a href="/cookies" className="link-gold">politique de gestion des cookies</a>.</p>
      </LegalSection>

      <LegalSection title="Loi applicable">
        <p>Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.</p>
      </LegalSection>
    </article>
  );
}

export function PolitiqueConfidentialite() {
  useSEO({ title: "Politique de confidentialité", description: "Politique de confidentialité et traitement des données personnelles du site L'Entracte." });
  return (
    <article data-testid="privacy-page" className="px-6 md:px-12 lg:px-16 max-w-3xl mx-auto py-16 md:py-24">
      <p className="overline">RGPD</p>
      <h1 className="font-display text-4xl md:text-6xl mt-4 leading-tight text-[#203240]">Politique de confidentialité</h1>

      <LegalSection title="Responsable du traitement">
        <p>L'Entracte Restaurant & Réception<br/>3 rue de Versailles, 92410 Ville d'Avray<br/>restaurantlentractevda@gmail.com</p>
      </LegalSection>

      <LegalSection title="Données collectées">
        <p>Nous collectons les données suivantes uniquement lorsque vous nous les transmettez volontairement :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Formulaire de contact</strong> : nom, email, téléphone (optionnel), objet, message</li>
          <li><strong>Demande de devis événement</strong> : nom, email, téléphone, type d'événement, date, nombre de convives, message</li>
          <li><strong>Réservation</strong> : gérée par notre prestataire Zenchef (voir leur politique de confidentialité)</li>
          <li><strong>Statistiques de visite</strong> : Google Analytics (après votre consentement) — pages visitées, durée, source, type d'appareil, avec adresse IP anonymisée</li>
        </ul>
      </LegalSection>

      <LegalSection title="Finalités">
        <ul className="list-disc pl-6 space-y-1">
          <li>Répondre à vos demandes de contact et de devis</li>
          <li>Confirmer vos réservations</li>
          <li>Améliorer notre site web et nos services</li>
          <li>Respecter nos obligations légales et comptables</li>
        </ul>
      </LegalSection>

      <LegalSection title="Base légale">
        <p>Le traitement de vos données repose sur :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Votre consentement</strong> (formulaires, cookies analytics)</li>
          <li><strong>L'exécution d'un contrat</strong> (réservation, événement)</li>
          <li><strong>Notre intérêt légitime</strong> (amélioration du service)</li>
        </ul>
      </LegalSection>

      <LegalSection title="Destinataires">
        <p>Vos données sont destinées uniquement à L'Entracte et à ses prestataires techniques (Zenchef pour les réservations, Google pour les statistiques anonymisées, Trustindex pour la collecte d'avis Google). Aucune donnée n'est vendue ni transmise à des fins commerciales.</p>
      </LegalSection>

      <LegalSection title="Durée de conservation">
        <ul className="list-disc pl-6 space-y-1">
          <li>Messages de contact : 3 ans</li>
          <li>Demandes de devis : 3 ans</li>
          <li>Statistiques Google Analytics : 14 mois (paramétrage standard)</li>
        </ul>
      </LegalSection>

      <LegalSection title="Vos droits">
        <p>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité, de limitation et d'opposition au traitement de vos données.</p>
        <p>Pour exercer ces droits, écrivez-nous à <a href="mailto:restaurantlentractevda@gmail.com" className="link-gold">restaurantlentractevda@gmail.com</a>.</p>
        <p>Vous pouvez également déposer une réclamation auprès de la <a href="https://www.cnil.fr/fr/plaintes" className="link-gold" target="_blank" rel="noopener noreferrer">CNIL</a>.</p>
      </LegalSection>
    </article>
  );
}

export function Cookies() {
  useSEO({ title: "Cookies", description: "Politique de gestion des cookies du site L'Entracte." });

  const resetConsent = () => {
    localStorage.removeItem("entracte_cookie_consent");
    window.location.reload();
  };

  return (
    <article data-testid="cookies-page" className="px-6 md:px-12 lg:px-16 max-w-3xl mx-auto py-16 md:py-24">
      <p className="overline">Vie privée</p>
      <h1 className="font-display text-4xl md:text-6xl mt-4 leading-tight text-[#203240]">Politique de cookies</h1>

      <LegalSection title="Qu'est-ce qu'un cookie ?">
        <p>Un cookie est un petit fichier déposé sur votre appareil par le navigateur lors de votre visite, permettant de mémoriser certaines informations (préférences, statistiques de visite, etc.).</p>
      </LegalSection>

      <LegalSection title="Cookies utilisés sur ce site">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="border-b border-[#D9D3C5] text-left text-[#203240]">
              <th className="py-2 pr-4">Nom</th><th className="py-2 pr-4">Émetteur</th><th className="py-2 pr-4">Finalité</th><th className="py-2">Durée</th>
            </tr></thead>
            <tbody className="text-[#3D4A5A]">
              <tr className="border-b border-[#D9D3C5]/50"><td className="py-2 pr-4">entracte_cookie_consent</td><td className="py-2 pr-4">L'Entracte</td><td className="py-2 pr-4">Mémoriser votre choix concernant les cookies</td><td className="py-2">12 mois</td></tr>
              <tr className="border-b border-[#D9D3C5]/50"><td className="py-2 pr-4">_ga, _ga_*</td><td className="py-2 pr-4">Google Analytics</td><td className="py-2 pr-4">Statistiques anonymisées (sous consentement)</td><td className="py-2">14 mois</td></tr>
              <tr className="border-b border-[#D9D3C5]/50"><td className="py-2 pr-4">zc_*</td><td className="py-2 pr-4">Zenchef</td><td className="py-2 pr-4">Réservation en ligne</td><td className="py-2">Session</td></tr>
              <tr><td className="py-2 pr-4">ti_*</td><td className="py-2 pr-4">Trustindex</td><td className="py-2 pr-4">Affichage des avis Google</td><td className="py-2">Session</td></tr>
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection title="Gérer mes préférences">
        <p>Vous pouvez à tout moment modifier vos choix concernant les cookies :</p>
        <button onClick={resetConsent} className="btn-ghost mt-4" data-testid="reset-cookie-consent">
          Modifier mes préférences cookies
        </button>
        <p className="text-sm text-[#697585] mt-3">Cliquer ouvrira à nouveau le bandeau de consentement lors du prochain rechargement.</p>
      </LegalSection>

      <LegalSection title="Désactiver les cookies via votre navigateur">
        <p>Vous pouvez également configurer votre navigateur pour bloquer ou supprimer les cookies. Consultez la documentation de votre navigateur (<a className="link-gold" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>, <a className="link-gold" href="https://support.mozilla.org/fr/kb/protection-renforcee-contre-pistage-firefox-ordinateur" target="_blank" rel="noopener noreferrer">Firefox</a>, <a className="link-gold" href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>).</p>
      </LegalSection>
    </article>
  );
}
